import { WellnessStep, WellnessSessionDraft, ValidationResult } from './types';

const STORAGE_KEY = 'consultant-wellness-drafts';

// Generate unique IDs
export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Step management
export const createEmptyStep = (): WellnessStep => ({
  id: generateId(),
  title: '',
  instructions: '',
  duration: 60,
  isTimed: true,
  imageUrl: undefined,
});

export const addStep = (steps: WellnessStep[]): WellnessStep[] => {
  if (steps.length >= 10) return steps;
  return [...steps, createEmptyStep()];
};

export const removeStep = (steps: WellnessStep[], stepId: string): WellnessStep[] => {
  return steps.filter(step => step.id !== stepId);
};

export const updateStep = (
  steps: WellnessStep[],
  stepId: string,
  updates: Partial<WellnessStep>
): WellnessStep[] => {
  return steps.map(step =>
    step.id === stepId ? { ...step, ...updates } : step
  );
};

export const clampSteps = (steps: WellnessStep[]): WellnessStep[] => {
  return steps.slice(0, 10);
};

// Session draft management
export const createEmptyDraft = (): WellnessSessionDraft => ({
  id: generateId(),
  title: '',
  duration: 15,
  category: 'physical',
  description: '',
  steps: [createEmptyStep()],
  videoUrl: '',
  difficulty: 'Intermediate',
  breathingTimer: true,
  showProgress: true,
  audioFile: undefined,
  themeColor: '#2bd4bd',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

export const saveDraftToStorage = (draft: WellnessSessionDraft): void => {
  try {
    const drafts = getDraftsFromStorage();
    const existingIndex = drafts.findIndex(d => d.id === draft.id);

    const updatedDraft = {
      ...draft,
      updatedAt: new Date().toISOString()
    };

    if (existingIndex >= 0) {
      drafts[existingIndex] = updatedDraft;
    } else {
      drafts.push(updatedDraft);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  } catch (error) {

  }
};

export const getDraftsFromStorage = (): WellnessSessionDraft[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {

    return [];
  }
};

export const loadDraftFromStorage = (draftId: string): WellnessSessionDraft | null => {
  const drafts = getDraftsFromStorage();
  return drafts.find(d => d.id === draftId) || null;
};

export const deleteDraftFromStorage = (draftId: string): void => {
  try {
    const drafts = getDraftsFromStorage().filter(d => d.id !== draftId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  } catch (error) {

  }
};

// Validation
export const validateDraft = (draft: WellnessSessionDraft): ValidationResult => {
  const errors: string[] = [];

  if (!draft.title.trim()) {
    errors.push('Session title is required');
  }

  if (draft.duration <= 0) {
    errors.push('Duration must be greater than 0');
  }

  if (!draft.description.trim()) {
    errors.push('Session description is required');
  }

  if (draft.steps.length === 0) {
    errors.push('At least one step is required');
  }

  draft.steps.forEach((step, index) => {
    if (!step.title.trim()) {
      errors.push(`Step ${index + 1}: Title is required`);
    }
    if (!step.instructions.trim()) {
      errors.push(`Step ${index + 1}: Instructions are required`);
    }
    if (step.duration <= 0) {
      errors.push(`Step ${index + 1}: Duration must be greater than 0`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Completion percentage calculation
export const computeCompletionPercent = (draft: WellnessSessionDraft): number => {
  let completedFields = 0;
  const totalFields = 6; // title, duration, category, description, at least 1 step, steps validation

  if (draft.title.trim()) completedFields++;
  if (draft.duration > 0) completedFields++;
  if (draft.category) completedFields++;
  if (draft.description.trim()) completedFields++;
  if (draft.steps.length > 0) completedFields++;

  // Check if all steps are valid
  const stepErrors = draft.steps.filter(step =>
    !step.title.trim() || !step.instructions.trim() || step.duration <= 0
  );
  if (stepErrors.length === 0) completedFields++;

  return Math.round((completedFields / totalFields) * 100);
};
